import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

function App() {
  // State für das hochgeladene Bild (als Data URL), die Overlay-Auswahl und das Ergebnisbild
  const [uploadedImageDataUrl, setUploadedImageDataUrl] = useState(null);
  const [overlayChoice, setOverlayChoice] = useState('1'); // Standardmäßig Overlay "1"
  const [resultImage, setResultImage] = useState(null);

  // react-dropzone: onDrop-Handler
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = () => {
        setUploadedImageDataUrl(reader.result);
        setResultImage(null); // Falls bereits ein Ergebnis vorhanden war, zurücksetzen
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*',
  });

  // Funktion, die das Profilbild generiert:
  // 1. Es wird ein 900x900 Canvas erstellt (weißer Hintergrund).
  // 2. Das hochgeladene Bild wird in den inneren Bereich (702x702 px, mit 99px Rand) skaliert.
  //    Dabei wird horizontal zentriert und vertikal so zugeschnitten, dass der untere Bildbereich vollständig erhalten bleibt.
  // 3. Anschließend wird das Overlay (900x900) aufgemalt.
  // 4. Das Ergebnis wird als Data URL gespeichert.
  const generateProfilePic = async () => {
    if (!uploadedImageDataUrl) return;

    // Canvas erstellen
    const canvas = document.createElement('canvas');
    canvas.width = 900;
    canvas.height = 900;
    const ctx = canvas.getContext('2d');

    // Hintergrund weiß füllen
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Hochgeladenes Bild laden
    const uploadedImg = new Image();
    uploadedImg.src = uploadedImageDataUrl;
    await new Promise((resolve) => {
      uploadedImg.onload = resolve;
    });

    // Zielbereich definieren: 702x702 Pixel, zentriert (Rand = 99px)
    const targetSize = 702;
    const margin = 99;
    const origW = uploadedImg.width;
    const origH = uploadedImg.height;
    // Scale-Faktor so, dass mindestens eine Dimension den Zielwert erreicht
    const scaleFactor = Math.max(targetSize / origW, targetSize / origH);
    const newW = origW * scaleFactor;
    const newH = origH * scaleFactor;

    // Bild in einem Offscreen-Canvas skalieren
    const offCanvas = document.createElement('canvas');
    offCanvas.width = newW;
    offCanvas.height = newH;
    const offCtx = offCanvas.getContext('2d');
    offCtx.drawImage(uploadedImg, 0, 0, newW, newH);

    // Berechnung des Ausschnitts:
    // Horizontal: zentriert
    const cropX = (newW - targetSize) / 2;
    // Vertikal: so, dass der untere Bereich erhalten bleibt
    const cropY = newH - targetSize;

    // Den zugeschnittenen Bereich in den Haupt-Canvas an Position (margin, margin) einfügen
    ctx.drawImage(
      offCanvas,
      cropX,
      cropY,
      targetSize,
      targetSize,
      margin,
      margin,
      targetSize,
      targetSize
    );

    // Overlay laden (verwende Overlay als png, z.B. "1.png", "2.png", …, "16.png")
    const overlayImg = new Image();
    overlayImg.src = process.env.PUBLIC_URL + '/' + overlayChoice + '.png';
    await new Promise((resolve) => {
      overlayImg.onload = resolve;
    });
    // Overlay über den kompletten Canvas zeichnen
    ctx.drawImage(overlayImg, 0, 0, canvas.width, canvas.height);

    // Ergebnis als Data URL speichern
    const dataUrl = canvas.toDataURL('image/png');
    setResultImage(dataUrl);
  };

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: 'auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
      }}
    >
      <h1>AfD Profilbild Generator</h1>

      {/* Bild hochladen */}
      <h2>1. Bild hochladen</h2>
      <div
        {...getRootProps()}
        style={{
          border: '2px dashed #cccccc',
          padding: '20px',
          textAlign: 'center',
          borderRadius: '10px',
          cursor: 'pointer',
        }}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Ziehen Sie eine Datei hierher ...</p>
        ) : (
          <p>
            Ziehen Sie eine Datei hierher oder klicken Sie, um eine Datei
            auszuwählen
          </p>
        )}
      </div>

      {uploadedImageDataUrl && (
        <div style={{ marginTop: '20px' }}>
          <h3>Vorschau des hochgeladenen Bildes</h3>
          <img
            src={uploadedImageDataUrl}
            alt="Hochgeladen"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>
      )}

      {/* Stil / Overlay auswählen mit Beispielbildern */}
      <div style={{ marginTop: '20px' }}>
        <h2>2. Stil auswählen</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {Array.from({ length: 16 }, (_, i) => i + 1).map((num) => (
            <div key={num} style={{ textAlign: 'center' }}>
              {/* Anzeige des Beispielbildes (z. B. "1.jpg") */}
              <img
                src={process.env.PUBLIC_URL + '/' + num + '.jpg'}
                alt={'Beispiel Profilbild ' + num}
                style={{
                  width: '100px',
                  height: '100px',
                  border:
                    overlayChoice === String(num)
                      ? '3px solid blue'
                      : '1px solid #ccc',
                  cursor: 'pointer',
                  borderRadius: '5px',
                }}
                onClick={() => setOverlayChoice(String(num))}
              />
              <div>
                {/* Radio-Button zur Auswahl des gewünschten Stils */}
                <input
                  type="radio"
                  id={`overlay${num}`}
                  name="overlay"
                  value={num}
                  checked={overlayChoice === String(num)}
                  onChange={(e) => setOverlayChoice(e.target.value)}
                />
                <label htmlFor={`overlay${num}`}>Profilbild {num}</label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export-Bereich */}
      <div style={{ marginTop: '20px' }}>
        <h2>3. Export</h2>
        <button
          onClick={generateProfilePic}
          disabled={!uploadedImageDataUrl}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            cursor: uploadedImageDataUrl ? 'pointer' : 'default',
          }}
        >
          Profilbild generieren
        </button>
      </div>

      {resultImage && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <h3>Generiertes Profilbild</h3>
          <img
            src={resultImage}
            alt="Generiertes Profilbild"
            style={{ width: '300px', height: '300px' }}
          />
          <div style={{ marginTop: '10px' }}>
            <a href={resultImage} download="profilbild.png">
              <button style={{ padding: '10px 20px', fontSize: '16px' }}>
                Bild herunterladen
              </button>
            </a>
          </div>
          <p>
            (Auf Smartphones können Sie lange auf das Bild drücken, um es
            herunterzuladen.)
          </p>
        </div>
      )}

      {/* Danksagung */}
      <div style={{ marginTop: '40px', textAlign: 'center' }}>
        <h2>Danksagung</h2>
        <p>Diese Website wurde ermöglicht durch:</p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            flexWrap: 'wrap',
          }}
        >
          <img
            src={process.env.PUBLIC_URL + '/banner1.jpg'}
            alt="Banner 1"
            style={{ width: '200px', height: 'auto' }}
          />
          <img
            src={process.env.PUBLIC_URL + '/banner2.jpg'}
            alt="Banner 2"
            style={{ width: '200px', height: 'auto' }}
          />
          <img
            src={process.env.PUBLIC_URL + '/banner3.jpg'}
            alt="Banner 3"
            style={{ width: '200px', height: 'auto' }}
          />
        </div>
      </div>

      {/* Impressum und Datenschutzerklärung */}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <hr style={{ width: '80%', margin: '20px auto' }} />
        <p>
          <a
            href="https://reinickendorf.afd.berlin/impressum/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginRight: '10px' }}
          >
            Impressum
          </a>
          |
          <a
            href="https://reinickendorf.afd.berlin/datenschutz/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: '10px' }}
          >
            Datenschutzerklärung
          </a>
        </p>
      </div>
    </div>
  );
}

export default App;
